import * as Sentry from '@sentry/react';
import { getAuthSession } from '../api/authSessionStore';
import { API_BASE_URL } from '../utils/constants';

const SENSITIVE_KEYWORDS = ['password', 'token', 'authorization', 'cookie', 'email', 'phone', 'gst', 'address'];
const FEATURE_PATH_PREFIXES = [
  { prefix: '/keyboard/invoices', feature: 'invoices' },
  { prefix: '/keyboard/quotations', feature: 'quotations' },
  { prefix: '/keyboard/estimated-invoices', feature: 'estimatedInvoices' },
  { prefix: '/keyboard/product', feature: 'products' },
  { prefix: '/keyboard/customers', feature: 'customers' },
  { prefix: '/keyboard/taxes', feature: 'taxes' },
  { prefix: '/keyboard/settings', feature: 'settings' },
  { prefix: '/keyboard/ledger', feature: 'ledger' },
  { prefix: '/invoices', feature: 'invoices' },
  { prefix: '/quotations', feature: 'quotations' },
  { prefix: '/estimated-invoices', feature: 'estimatedInvoices' },
  { prefix: '/products', feature: 'products' },
  { prefix: '/customers', feature: 'customers' },
  { prefix: '/taxes', feature: 'taxes' },
  { prefix: '/settings', feature: 'settings' },
  { prefix: '/ledger', feature: 'ledger' },
  { prefix: '/', feature: 'dashboard' },
];
const OPERATIONAL_SCOPE_PREFIXES = [
  { prefix: '/admin', scope: 'admin' },
  { prefix: '/tenants', scope: 'tenant' },
  { prefix: '/auth', scope: 'tenant' },
  { prefix: '/settings', scope: 'workspace' },
  { prefix: '/invoices', scope: 'workspace' },
  { prefix: '/quotation', scope: 'workspace' },
  { prefix: '/estimated-invoices', scope: 'workspace' },
  { prefix: '/products', scope: 'workspace' },
  { prefix: '/customers', scope: 'workspace' },
  { prefix: '/taxes', scope: 'workspace' },
  { prefix: '/payments', scope: 'workspace' },
];

let telemetryInitialized = false;
let globalHandlersRegistered = false;
let sentryEnabled = false;

function getBrowserLabel() {
  if (typeof navigator === 'undefined') return '';
  return navigator.userAgent || '';
}

function getDeviceLabel() {
  if (typeof navigator === 'undefined') return '';
  return navigator.userAgentData?.platform || navigator.platform || '';
}

function normalizeNumber(value, fallback) {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : fallback;
}

function getCurrentPathname() {
  if (typeof window === 'undefined') return '/';
  return window.location?.pathname || '/';
}

function detectFeature(pathname) {
  const value = pathname || '/';
  const match = FEATURE_PATH_PREFIXES.find((item) => value.startsWith(item.prefix));
  return match?.feature || 'unknown';
}

function detectMode(pathname) {
  return pathname?.startsWith('/keyboard') ? 'keyboard' : 'standard';
}

function isSensitiveKey(key = '') {
  const normalized = String(key).toLowerCase();
  return SENSITIVE_KEYWORDS.some((keyword) => normalized.includes(keyword));
}

function sanitizeValue(value, parentKey = '') {
  if (value === null || value === undefined) return value;

  if (Array.isArray(value)) {
    return value.map((item) => sanitizeValue(item, parentKey));
  }

  if (typeof value === 'object') {
    const result = {};
    Object.entries(value).forEach(([key, entryValue]) => {
      if (isSensitiveKey(key) || isSensitiveKey(parentKey)) {
        result[key] = '[REDACTED]';
      } else {
        result[key] = sanitizeValue(entryValue, key);
      }
    });
    return result;
  }

  if (typeof value === 'string' && isSensitiveKey(parentKey)) {
    return '[REDACTED]';
  }

  return value;
}

function scrubSentryEvent(event) {
  if (!event || typeof event !== 'object') return event;

  const cloned = { ...event };
  cloned.request = sanitizeValue(cloned.request);
  cloned.extra = sanitizeValue(cloned.extra);
  cloned.contexts = sanitizeValue(cloned.contexts);
  cloned.tags = sanitizeValue(cloned.tags);
  cloned.user = cloned.user
    ? {
        ...cloned.user,
        email: cloned.user.email ? '[REDACTED]' : undefined,
        username: cloned.user.username ? '[REDACTED]' : undefined,
      }
    : cloned.user;
  return cloned;
}

function createCorrelationId() {
  const cryptoApi = typeof window !== 'undefined' ? window.crypto : undefined;
  if (cryptoApi?.randomUUID) {
    return cryptoApi.randomUUID();
  }
  return `err-${Date.now()}-${Math.random().toString(16).slice(2, 10)}`;
}

function toError(errorLike) {
  if (errorLike instanceof Error) return errorLike;
  if (typeof errorLike === 'string') return new Error(errorLike);
  try {
    return new Error(JSON.stringify(errorLike));
  } catch (_error) {
    return new Error('Unknown client error');
  }
}

function resolveTracesSampleRate() {
  const configured = process.env.REACT_APP_SENTRY_TRACES_SAMPLE_RATE;
  if (configured) {
    return normalizeNumber(configured, 0.05);
  }
  return process.env.NODE_ENV === 'production' ? 0.05 : 0.2;
}

function getRelease() {
  return process.env.REACT_APP_SENTRY_RELEASE || process.env.REACT_APP_VERCEL_GIT_COMMIT_SHA || 'local';
}

function getEnvironment() {
  return process.env.REACT_APP_SENTRY_ENVIRONMENT || process.env.NODE_ENV || 'development';
}

function getTelemetryContext(overrides = {}) {
  const pathname = overrides.pathname || getCurrentPathname();
  const session = getAuthSession();
  return {
    pathname,
    feature: overrides.feature || detectFeature(pathname),
    mode: overrides.mode || detectMode(pathname),
    tenantId: overrides.tenantId || session?.tenantId || 'unknown',
    release: getRelease(),
  };
}

function sendClientErrorTelemetry(payload = {}) {
  if (typeof window === 'undefined' || typeof fetch !== 'function') {
    return;
  }

  const session = getAuthSession();
  const headers = {
    'Content-Type': 'application/json',
  };

  if (session?.tenantId) {
    headers['x-tenant-id'] = session.tenantId;
  }

  fetch(`${API_BASE_URL}/telemetry/client-errors`, {
    method: 'POST',
    credentials: 'include',
    headers,
    keepalive: true,
    body: JSON.stringify(payload),
  }).catch(() => {});
}

function detectOperationalScope(pathname = '') {
  const normalizedPath = String(pathname || '').trim().toLowerCase();
  const match = OPERATIONAL_SCOPE_PREFIXES.find((item) =>
    normalizedPath.startsWith(item.prefix)
  );
  return match?.scope || 'workspace';
}

/**
 * @typedef {Object} ClientCrashEvent
 * @property {string} errorId
 * @property {string} name
 * @property {string} message
 * @property {string} stack
 * @property {string} route
 * @property {string} feature
 * @property {string} mode
 * @property {string} tenantId
 * @property {string} release
 * @property {string} timestamp
 */

/**
 * Capture and forward client crashes to telemetry with route context.
 * @param {unknown} errorLike
 * @param {Record<string, unknown>} context
 * @returns {ClientCrashEvent}
 */
export function captureClientCrash(errorLike, context = {}) {
  const error = toError(errorLike);
  const telemetryContext = getTelemetryContext(context);
  const errorId = context.errorId || createCorrelationId();
  const sanitizedContext = sanitizeValue(context);

  /** @type {ClientCrashEvent} */
  const crashEvent = {
    errorId,
    name: error.name || 'Error',
    message: error.message || 'Unknown client error',
    stack: error.stack || '',
    route: telemetryContext.pathname,
    feature: telemetryContext.feature,
    mode: telemetryContext.mode,
    tenantId: telemetryContext.tenantId,
    release: telemetryContext.release,
    timestamp: new Date().toISOString(),
    browser: getBrowserLabel(),
    device: getDeviceLabel(),
  };

  if (sentryEnabled) {
    Sentry.withScope((scope) => {
      scope.setTag('error_id', crashEvent.errorId);
      scope.setTag('route', crashEvent.route);
      scope.setTag('feature', crashEvent.feature);
      scope.setTag('mode', crashEvent.mode);
      scope.setTag('tenant_id', crashEvent.tenantId);
      scope.setTag('release', crashEvent.release);
      scope.setContext('clientCrashEvent', sanitizeValue({ ...crashEvent, ...context }));
      Sentry.captureException(error);
    });
  }

  sendClientErrorTelemetry({
    ...crashEvent,
    source: context.source || context.boundaryScope || 'frontend',
    apiPath: context.apiPath || '',
    method: context.method || '',
    statusCode: context.statusCode || 0,
    errorCode: context.errorCode || '',
    context: sanitizedContext,
  });

  return crashEvent;
}

export function initTelemetry() {
  if (telemetryInitialized) return;
  telemetryInitialized = true;

  const dsn = process.env.REACT_APP_SENTRY_DSN;
  if (!dsn) return;

  Sentry.init({
    dsn,
    environment: getEnvironment(),
    release: getRelease(),
    tracesSampleRate: resolveTracesSampleRate(),
    beforeSend: scrubSentryEvent,
    initialScope: {
      tags: {
        app: 'bes_tally_frontend',
      },
    },
  });

  sentryEnabled = true;
}

export function registerGlobalErrorHandlers() {
  if (globalHandlersRegistered || typeof window === 'undefined') return;
  globalHandlersRegistered = true;

  window.addEventListener('error', (event) => {
    captureClientCrash(event.error || event.message, {
      source: 'window.error',
      filename: event.filename,
      lineno: event.lineno,
      colno: event.colno,
    });
  });

  window.addEventListener('unhandledrejection', (event) => {
    captureClientCrash(event.reason || 'Unhandled promise rejection', {
      source: 'window.unhandledrejection',
    });
  });
}

export function captureOperationalAction(payload = {}) {
  const pathname = String(payload.path || getCurrentPathname() || '').trim();
  if (!pathname) {
    return null;
  }

  const telemetryContext = getTelemetryContext({ pathname });
  const event = {
    scope: payload.scope || detectOperationalScope(pathname),
    action: payload.action || 'unknown.action',
    status: payload.status === 'failure' ? 'failure' : 'success',
    method: String(payload.method || 'GET').toUpperCase(),
    path: pathname,
    statusCode: Number(payload.statusCode || 0),
    durationMs: Number(payload.durationMs || 0),
    feature: telemetryContext.feature,
    mode: telemetryContext.mode,
    tenantId: telemetryContext.tenantId,
    release: telemetryContext.release,
    timestamp: new Date().toISOString(),
  };

  if (sentryEnabled) {
    Sentry.addBreadcrumb({
      category: 'operational-action',
      level: event.status === 'failure' ? 'warning' : 'info',
      message: `${event.method} ${event.path}`,
      data: sanitizeValue(event),
    });
  }

  return event;
}
